import { UIButton } from '../primitives';
import { useTranslation } from 'react-i18next';
import { formatAppDate } from '../../utils/dateFormat';

/**
 * PlanBlock — «Мой текущий план» карточка на Profile.
 *
 * Figma sources (redesign 2026-05-21):
 *  - 318:5925 (Free variant)
 *  - 318:5932 (Premium variant — orange diagonal tint, gradient text)
 *  - 318:5939 (Ultra variant   — purple diagonal tint, cyan→violet text)
 *
 * Common geometry across all tiers:
 *   width: FILL (370 inside body 402−16−16)
 *   cornerRadius: 24
 *   padding: 24
 *   itemSpacing: 16
 *   stroke: gradient 135° rgba(102,102,102,0)→rgba(102,102,102,0.48), 1px
 *   header row: SPACE_BETWEEN — label left, expiry text right (no badge container)
 *   button: uiButton type=secondary size=md, FILL (322×40), padding 13/24, radius 20,
 *           fill glass-16 + backdrop-blur 24, label «Сменить план»
 *
 * Tier-specific deltas (only the plan card; balance/ref unchanged):
 *   Free    — fill = same diagonal glass as balance/ref (white→grey-16),
 *             plan name plain white «Free».
 *   Premium — fill = white→#FE990E α=0.24 (orange halo),
 *             plan name «👑 Premium», gradient text #FFCC05→#FF9111.
 *   Ultra   — fill = white→#8A05FF α=0.24 (purple halo),
 *             plan name «💎 Ultra», gradient text #6CC9FF→#CB90FF.
 *
 * Note: tier-specific gradient stops are NOT bound to Figma variables — they are
 *       raw RGBA in the source design, so we keep them as named backgroundImage
 *       tokens (`plan-card-premium`, `plan-name-premium`, etc.) in figma-tokens.json.
 *       Emoji prefix («👑», «💎») is part of the plan-name text in Figma — there
 *       are no separate icon nodes.
 *
 * Props:
 *  - plan       ('FREE' | 'PREMIUM' | 'ULTRA')
 *  - expiresAt  (ISO string | null) — выводится как «До ДД.ММ.ГГГГ» в правой части header
 *  - onChangePlan () → void — navigate('/plans')
 */

const PLAN_TIER = {
  FREE: {
    surfaceClass: 'plan-card-free',
    nameLabel: 'Free',
    nameClass: 'text-text-primary',
  },
  PREMIUM: {
    surfaceClass: 'plan-card-premium',
    nameLabel: '\u{1F451} Premium',
    nameClass: 'plan-name-premium',
  },
  ULTRA: {
    surfaceClass: 'plan-card-ultra',
    nameLabel: '\u{1F48E} Ultra',
    nameClass: 'plan-name-ultra',
  },
};

function formatExpiryDate(isoString, t) {
  if (!isoString) return null;
  const formatted = formatAppDate(isoString);
  return formatted ? t('profile.activeUntil', { date: formatted }) : null;
}

export default function PlanBlock({
  plan = 'FREE',
  expiresAt = null,
  onChangePlan,
  disabled = false,
}) {
  const { t } = useTranslation();
  const planKey = (typeof plan === 'string' ? plan : 'FREE').toUpperCase();
  const tier = PLAN_TIER[planKey] || PLAN_TIER.FREE;
  const expiryLabel = planKey === 'FREE' ? null : formatExpiryDate(expiresAt, t);

  return (
    <div
      className={[
        'rounded-home-24 p-6 flex flex-col gap-4',
        tier.surfaceClass,
      ].join(' ')}
    >
      {/* title: label + plan-name + expiry  */}
      <div className="flex flex-col gap-2">
        {/* header row (SPACE_BETWEEN): label left / expiry right */}
        <div className="flex items-center justify-between gap-2">
          <span className="font-sans font-bold text-h2 text-text-secondary">
            {t('profile.currentPlan')}
          </span>
          {expiryLabel && (
            <span className="font-sans font-medium text-plan-label text-text-secondary text-right">
              {expiryLabel}
            </span>
          )}
        </div>

        {/* plan name (Free / 👑 Premium / 💎 Ultra) — 24/700, gradient text for Premium/Ultra */}
        <span
          className={['font-sans font-bold text-[24px] leading-none', tier.nameClass].join(' ')}
        >
          {tier.nameLabel}
        </span>
      </div>

      <UIButton
        type="secondary"
        size="md"
        onClick={onChangePlan}
        disabled={disabled}
        aria-label={t('profile.changePlan')}
        className="w-full"
      >
        {t('profile.changePlan')}
      </UIButton>
    </div>
  );
}
